import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';
import useAsyncEffect from 'use-async-effect';
import BrowserUrlDialog from '../BrowserUrlDialog';
import { MytaverseLogger } from '@helpers/logger';

import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';

import {
  useGetWebsocketMsg,
  useHandleAvatarWebsocketMsg,
  useHandleBrowserUrlRequestWebsocketMsg,
  useHandleEventSettingsMsg,
  useHandleEventWebsocketMsg,
  useHandleFollowMeWebsocketMsg,
  useHandleJoinedToRegionWebsocketMsg,
  useHandleLogoutRequestMsg,
  useHandleMuteParticipantWebsocketMsg,
  useHandleRaiseHandWebsocketMsg,
  useHandleScreenshotWebsocketMsg,
  useHandleTeleportingModalWebsocketMsg,
  useHandleUELogWebsocketMsg,
  useHandleWebsocketMsg,
} from '../hooks';

import { useClientHandleWebsocketMsg } from '@hooks/clientSpecificHooks';

import {
  mytaverseWebsocketLogger,
  useMytaverse,
} from '@providers/MytaverseProvider';

import { useAvatarMessage } from '@core/dashboard/providers/MytaverseEventProvider/hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
// import { useDeviceChange } from '@core/devices/hooks/devices';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import ROUTES from '@constants/routes';
import { toUELanguageCode } from '@constants/language';
import { env } from 'env';

const DashboardWebsocket = () => {
  const {
    sendJSONMessageToWebSocket,
    sendMessageToUnreal,
    getWebsocketUrl,
    getWebSocketOptions,
    customAvatarUrl,
    token,
    websocketSessionId,
    selectedLanguage,
  } = useMytaverse();

  const navigate = useNavigate();

  const {
    setCurrentParticipant,
    currentRoom,
    muted,
    currentParticipant,
    currentEventParticipant,
    gameSound,
    currentSkin,
    currentEvent,
    isHideWebUI,
    isHideWebUIExceptAssistant,
    isHideAllExceptTopBar,
    isUEModalActionInProgress,
    isHandRaised,
    gameliftStreamsSessionKey,
  } = useMytaverseEvent();

  const {
    ue5Token,
    user: currentUser,
    isCurrentParticipantSpeaking,
    conferenceName,
    isCameraOn,
  } = useCommunications();

  const { avatarMessage } = useAvatarMessage({
    currentSkin,
    currentEvent,
    customAvatarUrl,
  });

  const getWebsocketMsg = useGetWebsocketMsg();

  const { readyState: websocketReadyState, lastJsonMessage } = useWebSocket(
    getWebsocketUrl,
    getWebSocketOptions({ withReconnection: false }),
  );

  const websocketMsg = getWebsocketMsg(
    lastJsonMessage as LastWebsocketJsonMessageType,
  );

  const { openBrowserUrlDialog, setOpenBrowserUrlDialog } =
    useHandleBrowserUrlRequestWebsocketMsg(websocketMsg);
  useHandleWebsocketMsg(websocketMsg);
  useHandleEventWebsocketMsg(websocketMsg);
  useHandleEventSettingsMsg(websocketMsg);
  useHandleScreenshotWebsocketMsg(websocketMsg);
  useHandleJoinedToRegionWebsocketMsg(websocketMsg);
  useHandleMuteParticipantWebsocketMsg(websocketMsg);
  useHandleRaiseHandWebsocketMsg(websocketMsg);
  useHandleFollowMeWebsocketMsg(websocketMsg);
  useHandleTeleportingModalWebsocketMsg(websocketMsg);
  useHandleAvatarWebsocketMsg(websocketMsg);
  useHandleUELogWebsocketMsg(websocketMsg);
  useHandleLogoutRequestMsg(websocketMsg);
  useClientHandleWebsocketMsg(websocketMsg);

  const onSubmitBrowserUrl = async (url: string) => {
    await sendMessageToUnreal({ action: 'SET_BROWSER_URL', url });
    setOpenBrowserUrlDialog(false);
  };
  //useDeviceChange();

  const onCloseBrowserUrlDialog = () => setOpenBrowserUrlDialog(false);

  useEffect(() => {
    if (token) {
      const webSocketUrl = new URL(
        env.REACT_APP_SPATIAL_MANAGER_WEB_SOCKET_URL as string,
      );

      webSocketUrl.searchParams.append('token', token as string);
      webSocketUrl.searchParams.append('clientType', 'UE5');
      webSocketUrl.searchParams.append('session', websocketSessionId);

      if (gameliftStreamsSessionKey) {
        webSocketUrl.searchParams.append(
          'sessionKey',
          gameliftStreamsSessionKey,
        );
      }

      sendMessageToUnreal({
        action: 'WS_CONNECTION_URL',
        url: webSocketUrl,
        token,
      });
    }
  }, [token, gameliftStreamsSessionKey, websocketSessionId]);

  useEffect(() => {
    MytaverseLogger.log(
      `Check Avatar: ${avatarMessage ? JSON.stringify(avatarMessage) : null}`,
    );

    if (
      avatarMessage === null ||
      (avatarMessage && avatarMessage.downloadAvatarUrl)
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      MytaverseLogger.log(`No Avatar: ${JSON.stringify(avatarMessage)}`);
      navigate(ROUTES.SELECT_AVATAR_PROVIDER(currentEvent?.eventId));
    }, 5000);
    return () => {
      clearTimeout(timeout);
    };
  }, [avatarMessage, currentEvent?.eventId]);

  useAsyncEffect(() => {
    if (websocketReadyState === 1) {
      mytaverseWebsocketLogger.info('Start listening websocket messages');
      // Check if we are speaking to UE
      //startCommunicationFromUeTimer();
    }
  }, [websocketReadyState]);

  useEffect(() => {
    return () => {
      setCurrentParticipant((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          roomId: null,
          region: null,
          regions: [],
        };
      });

      sendJSONMessageToWebSocket({
        action: WebsocketAction.LeaveRoom,
      });
    };
  }, [sendJSONMessageToWebSocket]);

  const sendSyncDataWeb = useCallback(() => {
    if (!currentRoom) {
      return;
    }

    const message = {
      action: WebsocketAction.SyncDataWeb,
      speaking: isCurrentParticipantSpeaking || false,
      userLanguage: toUELanguageCode(selectedLanguage || 'en'),
      muted,
      raisedHand: isHandRaised,
      worldSoundLevel: gameSound === 0 ? 0.001 : gameSound,
      participantId: currentParticipant?.participantId,
      videoToken: ue5Token ?? '',
      videoConferenceName: conferenceName ?? '',
      videoUser: {
        ...currentUser,
        displayName:
          currentUser?.displayName ||
          currentParticipant?.name ||
          currentParticipant?.email,
      },
      videoOwnerWebcamStream:
        !isHideWebUI.flag &&
        !isHideWebUIExceptAssistant.flag &&
        !isHideAllExceptTopBar.flag &&
        isCameraOn &&
        currentUser?.userId
          ? {
              participantId: currentParticipant?.participantId ?? '',
              videoUserId: String(currentUser.userId),
            }
          : {
              participantId: '',
              videoUserId: '',
            },
      avatar: avatarMessage,
      isUEModalActionInProgress,
      navigationMode: currentEventParticipant?.navigationMode,
    };

    sendJSONMessageToWebSocket(message);
  }, [
    currentRoom,
    isCurrentParticipantSpeaking,
    currentParticipant,
    muted,
    isHandRaised,
    sendJSONMessageToWebSocket,
    gameSound,
    avatarMessage,
    ue5Token,
    conferenceName,
    currentUser,
    isCameraOn,
    isHideWebUI,
    isHideWebUIExceptAssistant,
    isHideAllExceptTopBar,
    isUEModalActionInProgress,
    currentEventParticipant?.navigationMode,
    selectedLanguage,
  ]);

  useEffect(() => {
    const interval = setInterval(async () => {
      await sendSyncDataWeb();
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [sendSyncDataWeb]);

  return openBrowserUrlDialog ? (
    <BrowserUrlDialog
      onClose={onCloseBrowserUrlDialog}
      onSubmit={onSubmitBrowserUrl}
    />
  ) : null;
};

export default DashboardWebsocket;
